import { ChevronRight, Replay, Info } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";
import { CardOutline } from "components/Card";
import { FeedbackStatusChip } from "components/Feedback";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import React, { FC, useMemo, useState } from "react";
import { Link as LinkBase, useNavigate } from "react-router-dom";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { ROLES, User } from "types/user";
import { SortOrder } from "helpers/constants";
import { getComparator } from "components/Table/utils";
import {
  getFeedbackDetailPath,
  getFeedbackStatus,
  shouldHideFeedback,
} from "../../../utils/feedback";
import ConfirmActionModal from "../../../modals/ConfirmActionModal/ConfirmActionModal";
import { useRequestFeedbackUtils } from "../../Feedback/Commom/useRequestFeedbackUtils";
import dayjs from "dayjs";
import { Requirement } from "types/requirement";
import {
  getFeedbackTypeLabel,
  getRequirementDisplayData,
} from "../../../utils/feedbackIter";

type RecentFeedbackActivityProps = {
  feedbacks: FeedbackType[];
  user: any;
  organizationId?: string;
  allRequirements?: Requirement[];
  /** When set (e.g. on learner detail), "View all" links here instead of the default portal/list */
  viewAllTo?: string;
};

const UserCell: FC<{ user: User }> = ({ user }) => (
  <Stack direction="row" sx={{ alignItems: "center" }}>
    <CoachPetAvatar
      pictureUrl={user?.picture_url}
      name={user?.name}
      equippedStoreItems={(user as any)?.equipped_store_items}
      avatarSize={28}
    />
    <Typography variant="body2" ml={1} noWrap>
      {user?.name}
    </Typography>
  </Stack>
);

export const RecentFeedbackActivity: FC<RecentFeedbackActivityProps> = ({
  feedbacks,
  user,
  organizationId,
  allRequirements = [],
  viewAllTo,
}) => {
  const navigate = useNavigate();
  const userRole = useMemo(() => user?.role, [user]);
  const { handleRetryFeedbackRequest } = useRequestFeedbackUtils();
  const [openSubRequirementsModal, setOpenSubRequirementsModal] =
    useState(false);
  const [selectedIterData, setSelectedIterData] = useState<{
    iterName: string;
    subRequirements: Array<{
      requirement_id: string;
      name: string;
      description?: string | null;
    }>;
    ratingsByRequirement?: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    >;
  } | null>(null);

  // Helper function to get requirement display name (ITER name for ITER feedbacks)
  const getRequirementDisplayName = (feedback: FeedbackType) => {
    return getRequirementDisplayData(feedback, allRequirements);
  };

  const buildRatingsByRequirementForIter = (iterName: string) => {
    const ratingsByRequirement: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    > = {};

    const history = feedbacks
      .filter((fb) => {
        const data = getRequirementDisplayData(fb, allRequirements);
        return data.isITER && data.name === iterName;
      })
      .sort((a, b) => {
        const da = a.observation_date || a.created_at || "";
        const db = b.observation_date || b.created_at || "";
        return da.localeCompare(db);
      });

    history.forEach((fb) => {
      const date = (fb.observation_date || fb.created_at) as string;
      fb.feedback_skills.forEach((skill) => {
        const reqId = skill.requirement?.requirement_id;
        if (!reqId) return;
        if (!ratingsByRequirement[reqId]) {
          ratingsByRequirement[reqId] = [];
        }
        ratingsByRequirement[reqId].push({
          date,
          learnerRating: skill.self_rating ?? null,
          coachRating: skill.coach_rating ?? null,
        });
      });
    });

    return ratingsByRequirement;
  };

  const getTypeChip = (feedback: FeedbackType) => {
    const label = getFeedbackTypeLabel(feedback);
    if (!label) return null;
    const color =
      label === "Competencies" ? "success" : label === "ITER" ? "info" : "primary";
    return <Chip label={label} size="small" color={color} />;
  };

  // Estados para el ordenamiento
  const [order, setOrder] = useState<SortOrder>(SortOrder.desc);
  const [orderBy, setOrderBy] = useState<string>("observation_date");
  const [showRetryDialog, setShowRetryDialog] = useState<boolean>(false);
  const [feedbackToRetry, setFeedbackToRetry] = useState<FeedbackType>(
    null as any,
  );

  // Función para manejar el ordenamiento
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === SortOrder.asc;
    setOrder(isAsc ? SortOrder.desc : SortOrder.asc);
    setOrderBy(property);
  };

  const handleRetry = async (feedback: FeedbackType) => {
    setShowRetryDialog(false);
    await handleRetryFeedbackRequest(feedback);
  };

  // Datos ordenados
  const sortedFeedbacks = useMemo(() => {
    const comparator = getComparator(order, orderBy);
    return [...feedbacks]
      .filter((feedback) => !shouldHideFeedback(feedback))
      .sort(comparator);
  }, [feedbacks, order, orderBy]);

  const columns = useMemo(() => {
    if (
      userRole === ROLES.ADMIN ||
      userRole === ROLES.DIRECTOR ||
      userRole === ROLES.MANAGER
    ) {
      return [
        { field: "requirement", headerName: "Requirement" },
        { field: "type", headerName: "Type" },
        { field: "observation_date", headerName: "Date" },
        { field: "student", headerName: "Learner" },
        { field: "coach", headerName: "Coach" },
        { field: "status", headerName: "Status" },
      ];
    } else if (userRole === ROLES.COACH) {
      return [
        { field: "requirement", headerName: "Requirement" },
        { field: "type", headerName: "Type" },
        { field: "observation_date", headerName: "Date" },
        { field: "student", headerName: "Learner" },
        { field: "status", headerName: "Status" },
      ];
    } else
      return [
        { field: "requirement", headerName: "Requirement" },
        { field: "type", headerName: "Type" },
        { field: "observation_date", headerName: "Date" },
        { field: "coach", headerName: "Coach" },
        { field: "status", headerName: "Status" },
      ];
  }, [userRole]);

  const linkPath = useMemo(() => {
    if (viewAllTo) return viewAllTo;
    if (userRole === ROLES.COACH || userRole === ROLES.STUDENT) {
      return "/feedback-portal";
    } else if (userRole === ROLES.ADMIN) {
      return "/feedbacks";
    } else if (
      (userRole === ROLES.MANAGER || userRole === ROLES.DIRECTOR) &&
      organizationId
    ) {
      return `/organization/${organizationId}/feedbacks`;
    }
    return "/feedbacks";
  }, [viewAllTo, userRole, organizationId]);

  const detailPath = (feedbackId: string) =>
    getFeedbackDetailPath(feedbackId, userRole, organizationId);
  const showDraftsLink =
    userRole === ROLES.COACH || userRole === ROLES.STUDENT;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom>
            Recent Feedback Activity
          </Typography>
          <Stack direction="row" spacing={2}>
            {showDraftsLink && (
              <Link
                sx={{ textDecoration: "none" }}
                component={LinkBase}
                to="/feedback-portal?tab=drafts"
              >
                DRAFTS
              </Link>
            )}
            <Link
              sx={{ textDecoration: "none" }}
              component={LinkBase}
              to={linkPath}
            >
              VIEW ALL
            </Link>
          </Stack>
        </Box>

        {/* Mobile View */}
        <Stack spacing={2} sx={{ display: { md: "none" } }}>
          {sortedFeedbacks.map((feedback) => (
            <CardOutline key={feedback.feedback_id}>
              <CardContent>
                <Stack spacing={0.5}>
                  <Typography variant="body1" fontWeight={600}>
                    Requirements:
                  </Typography>
                  {feedback.feedback_skills.length > 0 ? (
                    (() => {
                      const requirementData = getRequirementDisplayName(feedback);
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">{requirementData.name}</Typography>
                          {requirementData.isITER && requirementData.iterData && (
                            <Tooltip title="View Competencies">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedIterData({
                                    ...requirementData.iterData,
                                    ratingsByRequirement:
                                      buildRatingsByRequirementForIter(
                                        requirementData.name as string,
                                      ),
                                  });
                                  setOpenSubRequirementsModal(true);
                                }}
                              >
                                <Info fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })()
                  ) : (
                    <Typography variant="body1">No requirement</Typography>
                  )}
                  <Typography variant="body1" fontWeight={600}>
                    Type:
                  </Typography>
                  <Typography variant="body1">
                    {getTypeChip(feedback) || "N/A"}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Date:</strong>{" "}
                    {feedback.observation_date
                      ? dayjs(feedback.observation_date).format("MMM DD, YYYY")
                      : "-"}
                  </Typography>
                  {userRole !== ROLES.COACH && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">
                        <strong>Coach:</strong>
                      </Typography>
                      <CoachPetAvatar
                        pictureUrl={feedback?.coach?.picture_url}
                        name={feedback?.coach?.name}
                        equippedStoreItems={(feedback?.coach as any)?.equipped_store_items}
                        avatarSize={28}
                      />
                      <Typography variant="body1">{feedback?.coach?.name}</Typography>
                    </Stack>
                  )}
                  {userRole !== ROLES.STUDENT && (
                    <Typography variant="body1">
                      <strong>Learner:</strong> {feedback?.student?.name}
                    </Typography>
                  )}
                  <Typography variant="body1">
                    <strong>Status:</strong>{" "}
                    <FeedbackStatusChip feedback={feedback} />
                  </Typography>

                  <Box mt={2} display="flex" justifyContent="flex-end">
                    {(getFeedbackStatus(feedback) === FeedbackStatus.EXPIRED ||
                      getFeedbackStatus(feedback) ===
                        FeedbackStatus.DECLINED) &&
                      user.role === ROLES.STUDENT && (
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRetry(feedback);
                          }}
                          variant="text"
                          color="primary"
                          startIcon={<Replay />}
                          size="medium"
                        >
                          Retry
                        </Button>
                      )}
                  </Box>

                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      component={LinkBase}
                      to={detailPath(feedback.feedback_id)}
                      variant="text"
                      color="primary"
                      startIcon={<ChevronRight />}
                      size="small"
                    >
                      View Feedback
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </CardOutline>
          ))}
        </Stack>

        {/* Desktop View */}
        <Table
          size="small"
          sx={{
            display: { xs: "none", md: "table" },
            "& .MuiTableCell-root": { py: 0.5, px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((row) => (
                <TableCell key={row.field}>
                  {row.field === "status" || row.field === "observation_date" ? (
                    <TableSortLabel
                      active={orderBy === row.field}
                      direction={orderBy === row.field ? order : SortOrder.desc}
                      onClick={() => handleSort(row.field)}
                    >
                      {row.headerName}
                    </TableSortLabel>
                  ) : (
                    row.headerName
                  )}
                </TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFeedbacks.map((feedback) => (
              <TableRow
                key={feedback.feedback_id}
                sx={{
                  ":hover": { backgroundColor: "#f5f5f5", cursor: "pointer" },
                }}
                onClick={() => {
                  navigate(detailPath(feedback.feedback_id));
                }}
              >
                {columns.map((column) => (
                  <TableCell key={`${feedback.feedback_id}_${column.field}`}>
                    {column.field === "requirement" &&
                      feedback.feedback_skills.length > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {(() => {
                            const requirementData = getRequirementDisplayName(feedback);
                            return (
                              <>
                                <Typography variant="body2">{requirementData.name}</Typography>
                                {requirementData.isITER && requirementData.iterData && (
                                  <Tooltip title="View Competencies">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedIterData({
                                          ...requirementData.iterData,
                                          ratingsByRequirement:
                                            buildRatingsByRequirementForIter(
                                              requirementData.name as string,
                                            ),
                                        });
                                        setOpenSubRequirementsModal(true);
                                      }}
                                    >
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    {column.field === "requirement" &&
                      feedback.feedback_skills.length === 0 && (
                        <Typography variant="body2" color={"secondary"}>
                          No requirement
                        </Typography>
                      )}
                    {column.field === "type" && (
                      <Box>{getTypeChip(feedback) || "N/A"}</Box>
                    )}
                    {column.field === "observation_date" && (
                      <Typography variant="body2">
                        {feedback.observation_date
                          ? dayjs(feedback.observation_date).format("MMM DD, YYYY")
                          : "-"}
                      </Typography>
                    )}
                    {column.field === "student" && (
                      <UserCell user={feedback.student} />
                    )}
                    {column.field === "coach" && (
                      <UserCell user={feedback.coach} />
                    )}
                    {column.field === "status" && (
                      <FeedbackStatusChip feedback={feedback} />
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  {(getFeedbackStatus(feedback) === FeedbackStatus.EXPIRED ||
                    getFeedbackStatus(feedback) === FeedbackStatus.DECLINED) &&
                    user.role === ROLES.STUDENT && (
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          setFeedbackToRetry(feedback);
                          setShowRetryDialog(true);
                        }}
                        variant="text"
                        color="primary"
                        startIcon={<Replay />}
                        size="small"
                      >
                        Retry
                      </Button>
                    )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {showRetryDialog && (
        <ConfirmActionModal
          title={"Retry Feedback Request"}
          message={"Do you want to retry this Feedback request?"}
          show={showRetryDialog}
          onConfirm={() => handleRetry(feedbackToRetry)}
          onDecline={() => setShowRetryDialog(false)}
          onDismiss={() => {}}
          confirmButtonText="I Accept"
          declineButtonText="Decline"
        />
      )}

      {selectedIterData && (
        <SubRequirementsModal
          open={openSubRequirementsModal}
          onClose={() => {
            setOpenSubRequirementsModal(false);
            setSelectedIterData(null);
          }}
          iterName={selectedIterData.iterName}
          subRequirements={selectedIterData.subRequirements}
          ratingsByRequirement={selectedIterData.ratingsByRequirement}
        />
      )}
    </Card>
  );
};
